import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Play, Sparkles, Zap, Users, BookOpen } from "lucide-react";

interface GuestModeHeroProps {
  onPlayNow: () => void;
  className?: string;
}

export function GuestModeHero({ onPlayNow, className }: GuestModeHeroProps) {
  return (
    <Card className={`relative overflow-hidden p-8 bg-gradient-to-br from-teal-100 via-emerald-50 to-cyan-100 dark:from-purple-900/40 dark:via-pink-900/25 dark:to-cyan-900/30 border-2 border-primary/30 shadow-vibrant dark:animate-magic-glow ${className}`}>
      {/* Animated Decorative Elements - light mode uses soft teal orbs */}
      <div className="absolute top-4 right-4 w-20 h-20 bg-gradient-to-br from-teal-200/50 to-emerald-200/30 dark:from-teen-gold/50 dark:to-teen-orange/30 rounded-full blur-lg animate-float"></div>
      <div className="absolute bottom-6 left-6 w-16 h-16 bg-gradient-to-br from-cyan-200/40 to-teal-200/30 dark:from-teen-pink/40 dark:to-teen-purple/30 rounded-full blur-lg animate-float" style={{ animationDelay: '1.5s' }}></div>
      <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-gradient-to-br from-emerald-200/30 to-teal-200/20 dark:from-teen-cyan/30 dark:to-teen-blue/20 rounded-full blur-md animate-float" style={{ animationDelay: '2.5s' }}></div>
      
      {/* Sparkle decorations - only in dark mode */}
      <span className="hidden dark:inline absolute top-[10%] left-[15%] text-xl animate-twinkle" style={{ animationDelay: '0s' }}>✨</span>
      <span className="hidden dark:inline absolute top-[20%] right-[10%] text-2xl animate-twinkle" style={{ animationDelay: '0.7s' }}>⭐</span>
      <span className="hidden dark:inline absolute bottom-[25%] left-[8%] text-lg animate-twinkle" style={{ animationDelay: '1.4s' }}>💫</span>
      <span className="hidden dark:inline absolute bottom-[15%] right-[20%] text-xl animate-twinkle" style={{ animationDelay: '2.1s' }}>🌟</span>
      
      <div className="relative z-10 text-center space-y-6">
        {/* Hero Badge */}
        <Badge className="text-sm font-bold px-4 py-2 bg-gradient-to-r from-teen-gold to-teen-orange text-black shadow-lg">
          <Sparkles className="w-4 h-4 mr-2" />
          Try ChessHatch FREE - No Signup Required!
        </Badge>

        {/* Main Message */}
        <div className="space-y-3">
          <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-teen-cyan via-teen-purple to-teen-pink bg-clip-text text-transparent">
            Start Playing Chess Instantly! ⚡
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Jump into the action immediately! No registration needed to experience next-gen chess.
          </p>
        </div>

        {/* Quick Features - Glass morphism */}
        <div className="flex flex-wrap items-center justify-center gap-4 text-sm">
          <div className="flex items-center gap-2 bg-white/60 dark:bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-teen-green/30 shadow-sm">
            <Play className="w-4 h-4 text-teen-green" />
            <span className="font-medium text-teen-green">Instant AI Matches</span>
          </div>
          <div className="flex items-center gap-2 bg-white/60 dark:bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-teen-blue/30 shadow-sm">
            <BookOpen className="w-4 h-4 text-teen-blue" />
            <span className="font-medium text-teen-blue">AI Bot Training</span>
          </div>
          <div className="flex items-center gap-2 bg-white/60 dark:bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-teen-purple/30 shadow-sm">
            <Users className="w-4 h-4 text-teen-purple" />
            <span className="font-medium text-teen-purple">Safe Environment</span>
          </div>
        </div>

        {/* CTA Buttons - Vibrant */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
          <Button 
            onClick={onPlayNow}
            size="lg"
            className="px-8 py-4 text-lg font-bold rounded-xl bg-gradient-to-r from-teen-pink via-teen-purple to-teen-cyan text-white hover:from-teen-pink/90 hover:via-teen-purple/90 hover:to-teen-cyan/90 shadow-lg shadow-teen-purple/30 hover:shadow-teen-purple/50 hover:scale-105 transition-all duration-300"
          >
            <Zap className="w-5 h-5 mr-2" />
            Play Chess Now! 🎯
          </Button>
        </div>

        {/* Guest Benefits */}
        <div className="pt-4 border-t border-primary/20">
          <p className="text-sm text-muted-foreground mb-3">
            <strong className="bg-gradient-to-r from-teen-cyan to-teen-purple bg-clip-text text-transparent">Guest players get:</strong> AI opponents • Hatch Evolution • Bot challenges
          </p>
          <p className="text-xs text-muted-foreground">
            Create a free account later to save progress and unlock multiplayer matches!
          </p>
        </div>
      </div>
    </Card>
  );
}